import { useEffect, useRef } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import type { Message } from '../../../types/chat';

interface ChatWindowProps {
  messages: Message[];
  isTyping: boolean;
}

export default function ChatWindow({ messages, isTyping }: ChatWindowProps) {
  const { theme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll automático a nuevos mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 h-full">
      {messages.length === 0 ? (
        // Estado vacío
        <div className="h-full flex flex-col items-center justify-center px-6 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20'
              : 'bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100'
          }`}>
            <i className={`ri-chat-3-line text-3xl ${
              theme === 'dark'
                ? 'text-purple-400'
                : 'text-purple-500'
            }`}></i>
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
           How can I help you?
          </h3>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-500'
          }`}>
            Write your question and I will answer you as soon as possible.
          </p>
        </div>
      ) : (
        <>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-2 max-w-[85%] ${
                message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
              }`}>
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'assistant'
                    ? theme === 'dark'
                      ? 'bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500'
                      : 'bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400'
                    : theme === 'dark'
                      ? 'bg-white/10'
                      : 'bg-gray-200'
                }`}>
                  <i className={`${
                    message.role === 'assistant' ? 'ri-robot-2-line' : 'ri-user-3-line'
                  } text-sm ${
                    message.role === 'assistant'
                      ? 'text-white'
                      : theme === 'dark'
                        ? 'text-white/80'
                        : 'text-gray-600'
                  }`}></i>
                </div>

                {/* Burbuja de mensaje */}
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.role === 'assistant'
                      ? theme === 'dark'
                        ? 'bg-white/5 backdrop-blur-sm border border-white/10'
                        : 'bg-white shadow-sm border border-gray-200'
                      : theme === 'dark'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                        : 'bg-gradient-to-br from-purple-400 to-pink-400'
                  }`}
                >
                  <p className={`text-sm leading-relaxed ${
                    message.role === 'assistant'
                      ? theme === 'dark'
                        ? 'text-white/90'
                        : 'text-gray-800'
                      : 'text-white'
                  }`}>
                    <span dangerouslySetInnerHTML={{ __html: message.content }} />
                  </p>
                  
                  {/* Timestamp */}
                  <span className={`text-xs mt-1 block ${
                    message.role === 'assistant'
                      ? theme === 'dark'
                        ? 'text-white/40'
                        : 'text-gray-400'
                      : 'text-white/70'
                  }`}>
                    {new Date(message.timestamp).toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Estado "IA escribiendo" */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex gap-2 max-w-[85%]">
                {/* Avatar IA */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500'
                    : 'bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400'
                }`}>
                  <i className="ri-robot-2-line text-sm text-white"></i>
                </div>

                {/* Burbuja escribiendo */}
                <div className={`rounded-2xl px-4 py-3 ${
                  theme === 'dark'
                    ? 'bg-white/5 backdrop-blur-sm border border-white/10'
                    : 'bg-white shadow-sm border border-gray-200'
                }`}>
                  <div className="flex gap-1">
                    <span className={`w-2 h-2 rounded-full animate-bounce ${
                      theme === 'dark' ? 'bg-white/60' : 'bg-gray-400'
                    }`} style={{ animationDelay: '0ms' }}></span>
                    <span className={`w-2 h-2 rounded-full animate-bounce ${
                      theme === 'dark' ? 'bg-white/60' : 'bg-gray-400'
                    }`} style={{ animationDelay: '150ms' }}></span>
                    <span className={`w-2 h-2 rounded-full animate-bounce ${
                      theme === 'dark' ? 'bg-white/60' : 'bg-gray-400'
                    }`} style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Referencia para scroll automático */}
          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
}
